// Maintains stable identities for faces across frames so each person keeps one
// ID (and one analysis), debounces the lock state, and counts who is watching.

import { CONFIG } from "../config";
import type { FaceObservation, TrackedFace } from "../types";

export class Tracker {
  private tracks: TrackedFace[] = [];
  private nextId = 1;

  update(observations: FaceObservation[], now: number): TrackedFace[] {
    const claimed = new Set<number>();

    // 1. Match each existing track to the nearest unclaimed observation.
    for (const track of this.tracks) {
      let best = -1;
      let bestDist: number = CONFIG.tracker.matchRadius;
      for (let i = 0; i < observations.length; i++) {
        if (claimed.has(i)) continue;
        const o = observations[i]!;
        const d = Math.hypot(o.cx - track.cx, o.cy - track.cy);
        if (d < bestDist) {
          bestDist = d;
          best = i;
        }
      }
      if (best >= 0) {
        claimed.add(best);
        this.applyObservation(track, observations[best]!, now);
        track.missing = 0;
      } else {
        track.missing++;
      }
    }

    // 2. Spawn tracks for unclaimed observations.
    for (let i = 0; i < observations.length; i++) {
      if (claimed.has(i)) continue;
      this.tracks.push(this.spawn(observations[i]!, now));
    }

    // 3. Drop stale tracks.
    this.tracks = this.tracks.filter((t) => t.missing <= CONFIG.tracker.maxMissingFrames);

    return this.tracks;
  }

  /** Currently-visible faces that are locked onto the camera. */
  get watchers(): TrackedFace[] {
    return this.tracks.filter((t) => t.locked && t.missing === 0);
  }

  get visible(): TrackedFace[] {
    return this.tracks.filter((t) => t.missing === 0);
  }

  private applyObservation(track: TrackedFace, o: FaceObservation, now: number): void {
    // Update geometry/pose but preserve identity + analysis state.
    track.bbox = o.bbox;
    track.cx = o.cx;
    track.cy = o.cy;
    track.yaw = o.yaw;
    track.pitch = o.pitch;
    track.roll = o.roll;
    track.headFacing = o.headFacing;
    track.gazeCentered = o.gazeCentered;
    track.eyeOpen = o.eyeOpen;
    // Temporal EMA on the lock score before thresholding: smooths single-frame
    // dropouts on a moving camera, improving eye-contact precision.
    const a = CONFIG.lockOn.lockSmoothing;
    track.lockScore = a * o.lockScore + (1 - a) * track.lockScore;
    track.distanceM = o.distanceM;
    track.eyesBBox = o.eyesBBox;
    track.gazeYawRad = o.gazeYawRad;
    track.gazePitchRad = o.gazePitchRad;
    track.actions = o.actions;

    const rawLocked = track.lockScore > CONFIG.lockOn.lockThreshold;
    if (rawLocked === track.locked) {
      track.lockStreak = 0;
    } else {
      track.lockStreak++;
      if (track.lockStreak >= CONFIG.lockOn.lockHysteresis) {
        track.locked = rawLocked;
        track.lockStreak = 0;
        track.lockedSince = rawLocked ? now : 0;
      }
    }
  }

  private spawn(o: FaceObservation, now: number): TrackedFace {
    const rawLocked = o.lockScore > CONFIG.lockOn.lockThreshold;
    return {
      ...o,
      id: this.nextId++,
      locked: rawLocked,
      lockStreak: 0,
      missing: 0,
      lockedSince: rawLocked ? now : 0,
      lastAnalysedAt: 0,
      analysis: { status: "none" },
    };
  }
}
